import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import {
  Mail, MailX, Loader2, StickyNote, Phone, Voicemail, CalendarClock,
  Upload, FileText, DollarSign, CheckCircle2, XCircle, AlertTriangle, Activity,
  ArrowRightCircle, Send, Plus
} from "lucide-react";

const KIND_META = (kind) => {
  if (kind?.startsWith("email."))          return { Icon: Mail,        color: "bg-blue-100 text-blue-700" };
  if (kind?.startsWith("note."))           return { Icon: StickyNote,  color: "bg-amber-100 text-amber-700" };
  if (kind === "note.call_logged")         return { Icon: Phone,       color: "bg-amber-100 text-amber-700" };
  if (kind === "note.voicemail")           return { Icon: Voicemail,   color: "bg-amber-100 text-amber-700" };
  if (kind === "note.follow_up")           return { Icon: CalendarClock, color: "bg-purple-100 text-purple-700" };
  if (kind === "settlement_date_edit" || kind === "settlement_date_backfill")
                                           return { Icon: CalendarClock, color: "bg-teal-100 text-teal-700" };
  if (kind === "status_change" || kind?.includes("status_changed")) return { Icon: ArrowRightCircle, color: "bg-slate-100 text-slate-700" };
  if (kind?.includes("funded"))            return { Icon: DollarSign,  color: "bg-emerald-100 text-emerald-700" };
  if (kind?.includes("approved"))          return { Icon: CheckCircle2,color: "bg-green-100 text-green-700" };
  if (kind?.includes("declined"))          return { Icon: XCircle,     color: "bg-red-100 text-red-700" };
  if (kind?.includes("document"))          return { Icon: Upload,      color: "bg-indigo-100 text-indigo-700" };
  if (kind?.includes("payoff"))            return { Icon: FileText,    color: "bg-teal-100 text-teal-700" };
  if (kind?.includes("error"))             return { Icon: AlertTriangle, color: "bg-red-100 text-red-700" };
  return { Icon: Activity, color: "bg-slate-100 text-slate-700" };
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function ActivityTimeline({ loanId, applicationId, firmId, attorneyEmail, entityType = "loan" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);

  const fetchTimeline = useCallback(() => {
    const params = {};
    if (loanId) params.loan_id = loanId;
    if (applicationId) params.application_id = applicationId;
    if (firmId) params.firm_id = firmId;
    if (attorneyEmail) params.attorney_email = attorneyEmail;
    setLoading(true);
    api.get("/comms/timeline", { params })
      .then(r => setEvents(r.data || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [loanId, applicationId, firmId, attorneyEmail]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  return (
    <div data-testid="activity-timeline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Activity Timeline</h3>
        <button
          onClick={() => setShowAddNote(true)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0066FF] hover:text-[#0055D4]"
          data-testid="add-note-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add note
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading timeline…
        </div>
      ) : events.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center bg-slate-50/50">
          <Activity className="w-8 h-8 mx-auto text-slate-400 mb-2" />
          <div className="text-sm text-slate-500">No activity yet.</div>
          <div className="text-xs text-slate-400 mt-1">Every action, email, and note will appear here.</div>
        </div>
      ) : (
        <ol className="relative border-l-2 border-slate-100 ml-4 space-y-4">
          {events.map((ev, idx) => {
            const meta = KIND_META(ev.kind);
            const Icon = meta.Icon;
            return (
              <li key={ev.id || idx} className="relative pl-6" data-testid={`timeline-event-${idx}`}>
                <span className={`absolute -left-[13px] top-0 w-6 h-6 rounded-full flex items-center justify-center ${meta.color} ring-4 ring-white`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-sm font-medium text-slate-900">{ev.title}</div>
                  <time className="text-xs text-slate-500 whitespace-nowrap">{fmt(ev.created_at)}</time>
                </div>
                {ev.body && (
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{ev.body}</div>
                )}
                {ev.actor && ev.actor !== "system" && (
                  <div className="text-xs text-slate-400 mt-1">by {ev.actor}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {showAddNote && (
        <AddNoteDialog
          entityType={entityType}
          entityId={loanId || applicationId || firmId || attorneyEmail}
          loanId={loanId}
          applicationId={applicationId}
          firmId={firmId}
          attorneyEmail={attorneyEmail}
          onClose={() => setShowAddNote(false)}
          onSaved={() => { setShowAddNote(false); fetchTimeline(); }}
        />
      )}
    </div>
  );
}

function AddNoteDialog({ entityType, entityId, loanId, applicationId, firmId, attorneyEmail, onClose, onSaved }) {
  const [noteType, setNoteType] = useState("note");
  const [body, setBody] = useState("");
  const [followUpAt, setFollowUpAt] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await api.post("/comms/notes", {
        body: body.trim(),
        note_type: noteType,
        entity_type: entityType, entity_id: entityId,
        loan_id: loanId, application_id: applicationId, firm_id: firmId,
        attorney_email: attorneyEmail,
        follow_up_at: followUpAt || null,
      });
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Add note</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Type</label>
            <select value={noteType} onChange={e => setNoteType(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="note-type-select">
              <option value="note">Note</option>
              <option value="call_logged">Phone call</option>
              <option value="voicemail">Voicemail left</option>
              <option value="follow_up">Follow-up scheduled</option>
              <option value="meeting">Meeting</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Note</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={5}
              placeholder="e.g. Left voicemail — will call back Tuesday"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              data-testid="note-body-input" />
          </div>
          {noteType === "follow_up" && (
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">Follow-up date</label>
              <input type="datetime-local" value={followUpAt} onChange={e => setFollowUpAt(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={submit} disabled={saving || !body.trim()}
            className="px-4 py-2 bg-[#0066FF] text-white text-sm font-medium rounded-lg hover:bg-[#0055D4] disabled:opacity-50"
            data-testid="note-save-btn">
            {saving ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>
    </div>
  );
}
